/**
 * ApiTokensPage (Doc 60) — Personal Access Tokens management.
 */
import {
  AlertTriangle,
  CheckCircle2,
  Copy,
  KeyRound,
  Loader2,
  Plus,
  RefreshCw,
  Trash2,
  X,
} from "lucide-react";
import * as React from "react";

import { apiTokensApi, type ApiTokenItem, type CreateTokenResponse } from "@/api/apiTokens";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useUIStore } from "@/stores/ui";

function fmtDate(s: string | null): string {
  if (!s) return "—";
  return new Date(s).toLocaleString("vi-VN");
}

interface CreateDialogProps {
  onClose: () => void;
  onCreated: () => void;
}

function CreateDialog({ onClose, onCreated }: CreateDialogProps): JSX.Element {
  const pushToast = useUIStore((s) => s.pushToast);
  const [name, setName] = React.useState("");
  const [ttlDays, setTtlDays] = React.useState<number | null>(90);
  const [submitting, setSubmitting] = React.useState(false);
  const [result, setResult] = React.useState<CreateTokenResponse | null>(null);

  const handleCreate = async (): Promise<void> => {
    if (!name.trim()) return;
    setSubmitting(true);
    try {
      const r = await apiTokensApi.create(name.trim(), ttlDays);
      setResult(r);
      onCreated();
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Không tạo được token",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopy = async (): Promise<void> => {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result.raw_token);
      pushToast({
        variant: "success",
        title: "Đã copy raw token",
        description: "Lưu vào nơi an toàn — không hiển thị lại.",
      });
    } catch {
      // ignore
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <Card
        className="w-full max-w-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <CardContent className="p-5 space-y-3">
          <div className="flex items-start justify-between">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <KeyRound className="size-5" /> Tạo Personal Access Token
            </h2>
            <button
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground"
            >
              <X className="size-4" />
            </button>
          </div>

          {result === null ? (
            <>
              <p className="text-sm text-muted-foreground">
                Token cấp quyền tương đương account của bạn. Lưu cẩn thận
                vì sẽ chỉ hiển thị 1 lần.
              </p>
              <div className="space-y-2">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Tên token *
                  </label>
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="VD: cron-report-daily"
                    maxLength={128}
                    disabled={submitting}
                    autoFocus
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    TTL (ngày) — bỏ trống = không expire
                  </label>
                  <div className="flex gap-2">
                    {[7, 30, 90, 180, 365, null].map((d) => (
                      <Button
                        key={d ?? "none"}
                        size="sm"
                        variant={ttlDays === d ? "default" : "outline"}
                        onClick={() => setTtlDays(d)}
                        disabled={submitting}
                      >
                        {d === null ? "Không expire" : `${d}d`}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={onClose}
                  disabled={submitting}
                >
                  Hủy
                </Button>
                <Button
                  onClick={() => void handleCreate()}
                  disabled={submitting || !name.trim()}
                >
                  {submitting ? (
                    <Loader2 className="size-4 animate-spin mr-1.5" />
                  ) : (
                    <Plus className="size-4 mr-1.5" />
                  )}
                  Tạo
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="rounded-md bg-amber-50 dark:bg-amber-950/30 border-l-4 border-amber-500 p-3 text-sm">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="size-4 text-amber-600 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium">Lưu ngay token này</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Token sẽ KHÔNG được hiển thị lại sau khi đóng dialog.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium">
                  Raw token (copy ngay)
                </label>
                <div className="flex gap-2">
                  <Input
                    value={result.raw_token}
                    readOnly
                    className="flex-1 font-mono text-xs"
                    onFocus={(e) => e.target.select()}
                  />
                  <Button onClick={() => void handleCopy()}>
                    <Copy className="size-4 mr-1.5" /> Copy
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Sử dụng trong header:{" "}
                  <code className="text-[10px]">
                    Authorization: Bearer {result.prefix}...
                  </code>
                </p>
              </div>

              <div className="text-xs text-muted-foreground space-y-0.5">
                <div>Tên: <strong>{result.name}</strong></div>
                <div>Tạo: {fmtDate(result.created_at)}</div>
                <div>
                  Expires:{" "}
                  {result.expires_at
                    ? fmtDate(result.expires_at)
                    : "Không expire"}
                </div>
              </div>

              <div className="flex justify-end pt-2">
                <Button onClick={onClose}>Đóng</Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export function ApiTokensPage(): JSX.Element {
  const pushToast = useUIStore((s) => s.pushToast);
  const [items, setItems] = React.useState<ApiTokenItem[]>([]);
  const [activeCount, setActiveCount] = React.useState(0);
  const [limit, setLimit] = React.useState(20);
  const [loading, setLoading] = React.useState(true);
  const [revokingId, setRevokingId] = React.useState<string | null>(null);
  const [createOpen, setCreateOpen] = React.useState(false);
  const [includeRevoked, setIncludeRevoked] = React.useState(false);

  const refresh = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiTokensApi.list(includeRevoked);
      setItems(r.items);
      setActiveCount(r.active_count);
      setLimit(r.limit);
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Không tải được tokens",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setLoading(false);
    }
  }, [includeRevoked, pushToast]);

  React.useEffect(() => {
    void refresh();
  }, [refresh]);

  const handleRevoke = async (item: ApiTokenItem): Promise<void> => {
    if (!window.confirm(`Revoke token "${item.name}"? Không hồi phục được.`)) {
      return;
    }
    setRevokingId(item.id);
    try {
      await apiTokensApi.revoke(item.id);
      pushToast({
        variant: "success",
        title: "Đã revoke token",
        description: item.name,
      });
      await refresh();
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Revoke thất bại",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setRevokingId(null);
    }
  };

  return (
    <div className="max-w-4xl space-y-5">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold flex items-center gap-2">
            <KeyRound className="size-5" /> Personal Access Tokens
          </h1>
          <p className="text-sm text-muted-foreground">
            Long-lived tokens cho script / CLI / integrations. Format{" "}
            <code className="text-xs">cuvai_xxx</code>. Doc 60.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">
            {activeCount} / {limit} active
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void refresh()}
          >
            <RefreshCw className="size-4 mr-1.5" /> Reload
          </Button>
          <Button
            size="sm"
            onClick={() => setCreateOpen(true)}
            disabled={activeCount >= limit}
          >
            <Plus className="size-4 mr-1.5" /> Tạo token
          </Button>
        </div>
      </header>

      <Card className="bg-muted/30 border-muted">
        <CardContent className="p-3 text-xs space-y-1">
          <p className="font-medium">Cách dùng:</p>
          <pre className="font-mono text-[10px] overflow-x-auto">
{`curl https://agent.ixgroup.vn/api/v1/me/profile \\
  -H "Authorization: Bearer cuvai_..."`}
          </pre>
          <p className="text-muted-foreground">
            Token có quyền tương đương account. Revoke ngay nếu nghi compromise.
          </p>
        </CardContent>
      </Card>

      <div className="flex items-center gap-2">
        <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer">
          <input
            type="checkbox"
            checked={includeRevoked}
            onChange={(e) => setIncludeRevoked(e.target.checked)}
          />
          Hiển thị đã revoke
        </label>
      </div>

      {loading ? (
        <Card>
          <CardContent className="p-6 flex items-center gap-2 text-muted-foreground">
            <Loader2 className="size-4 animate-spin" /> Đang tải…
          </CardContent>
        </Card>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground">
            <KeyRound className="size-8 mx-auto mb-2 opacity-50" />
            Chưa có token nào. Click <strong>Tạo token</strong> ở trên.
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-muted-foreground border-b bg-muted/40">
                <tr>
                  <th className="px-4 py-2">Tên + Prefix</th>
                  <th className="px-4 py-2">Created</th>
                  <th className="px-4 py-2">Last used</th>
                  <th className="px-4 py-2">Expires</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2 text-right"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr
                    key={it.id}
                    className={
                      "border-b last:border-0 " +
                      (it.is_active ? "" : "opacity-60")
                    }
                  >
                    <td className="px-4 py-3 align-top">
                      <div className="font-medium">{it.name}</div>
                      <div className="text-[10px] text-muted-foreground font-mono">
                        {it.prefix}…
                      </div>
                    </td>
                    <td className="px-4 py-3 align-top text-xs text-muted-foreground">
                      {fmtDate(it.created_at)}
                    </td>
                    <td className="px-4 py-3 align-top text-xs text-muted-foreground">
                      {fmtDate(it.last_used_at)}
                    </td>
                    <td className="px-4 py-3 align-top text-xs text-muted-foreground">
                      {it.expires_at ? fmtDate(it.expires_at) : "Không expire"}
                    </td>
                    <td className="px-4 py-3 align-top">
                      {it.is_active ? (
                        <span className="inline-flex items-center gap-1 text-xs text-green-700">
                          <CheckCircle2 className="size-3" /> Active
                        </span>
                      ) : it.revoked_at ? (
                        <span className="text-xs text-red-700">REVOKED</span>
                      ) : (
                        <span className="text-xs text-amber-700">EXPIRED</span>
                      )}
                    </td>
                    <td className="px-4 py-3 align-top text-right">
                      {it.is_active ? (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={revokingId === it.id}
                          onClick={() => void handleRevoke(it)}
                        >
                          {revokingId === it.id ? (
                            <Loader2 className="size-3 animate-spin mr-1" />
                          ) : (
                            <Trash2 className="size-3 mr-1" />
                          )}
                          Revoke
                        </Button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {createOpen ? (
        <CreateDialog
          onClose={() => setCreateOpen(false)}
          onCreated={() => void refresh()}
        />
      ) : null}
    </div>
  );
}
